import { Image, Typography } from 'antd'
import { TextInput } from 'components/form/inputs/TextInput'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import isNil from 'lodash/isNil'
import React, { FC, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoMeasurementUnit,
  INDICATION_3D_ZOOM_EVENT,
  Indication3DLocation,
} from 'types'

const MILLIMETERS_TO_INCHES_CONVERSION = 0.0393701
const MEASUREMENT_PRECISION_DIGITS = 4
const MEASUREMENT_LOCALE = 'en-US'

const { Text } = Typography

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 0.5rem;

  &:not(:first-child) {
    border-top: 1px solid #3a3a3a;
    padding-top: 1rem;
    margin-top: 1rem;
  }
`

const LocationSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
`

const LocationGroup = styled.div`
  display: contents;
`

const LocationGroupLabel = styled(Text)`
  grid-column: 1 / -1;
  margin: 0.5rem 0 0.1rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast?.defaultText || '#E0E0E0'};
`

const CommentsContainer = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: start;
  gap: 1rem;
`

const CommentLabel = styled(Text)`
  font-weight: 600;
  padding-top: 0.5rem;
  white-space: nowrap;
  color: ${({ theme }) => theme.forecast?.secondaryText || '#AAA'};
`

const CommentValue = styled.div`
  white-space: pre-wrap;
  max-height: 10rem;
  overflow-y: auto;
  padding: 0.5rem;
  background-color: ${({ theme }) =>
    theme.forecast?.cardGroupBackground || '#2C2C2E'};
  border-radius: 6px;
  color: ${({ theme }) => theme.forecast?.defaultText || '#FFF'};
  font-weight: 600;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  .ant-image {
    max-width: 100%;
    border-radius: 6px;
  }
`

interface NickAndDingIndicationCustomInfo {
  damage_mechanism?: string
  measurement?: number
  location?: Indication3DLocation
  comments?: string
  is_solution?: boolean
  created_at?: string
  zRange?: {
    deviation: [number, number]
  }
}

export const Title = () => 'Nick and Ding Indications'
export const Width = '24rem'

interface LocationGroup {
  label: string
  points: {
    label: string
    value: number | null | undefined
  }[]
  transform?: (num: number) => number
}

interface ProcessedLocationGroup {
  label: string
  points: {
    label: string
    value: string
  }[]
}

export const CardView: FC<IndicationDetailCardProps> = () => {
  const { indication: indicationItem } = useIndicationData()
  const customInfo = indicationItem?.customInfo as
    | NickAndDingIndicationCustomInfo
    | undefined

  // Image loading hooks
  const files = useRolodexFileLS(
    useMemo(
      () =>
        indicationItem
          ? {
              entry_id: indicationItem.id,
              path: '/',
            }
          : undefined,
      [indicationItem]
    )
  )

  const imageFileRef = useMemo(
    () => files.fileRefs.find((ref) => !ref.path.includes('/')),
    [files.fileRefs]
  )

  const { url: imageUrl } = useRolodexSignedURLs(
    useMemo(
      () =>
        indicationItem && imageFileRef
          ? [
              {
                entry_id: indicationItem.id,
                path: imageFileRef.path,
              },
            ]
          : [],
      [indicationItem, imageFileRef]
    )
  )

  useEffect(() => {
    if (customInfo?.location && indicationItem) {
      const zoomEvent = new CustomEvent(INDICATION_3D_ZOOM_EVENT, {
        detail: {
          location: customInfo.location,
          indicationId: indicationItem.id,
        },
      })
      window.dispatchEvent(zoomEvent)
    }
  }, [customInfo, indicationItem])

  if (!customInfo) {
    return (
      <div>
        <Text>
          No custom information available for this Nick and Ding indication.
        </Text>
      </div>
    )
  }

  const summaryData = [
    { label: 'Flaw Classification', value: customInfo.damage_mechanism },
    { label: 'Measurement (in.)', value: customInfo.measurement?.toFixed(4) },
  ]
    .filter((item) => !isNil(item.value))
    .map((item) => ({ ...item, value: String(item.value) }))

  const locationGroups: ProcessedLocationGroup[] = customInfo.location
    ? [
        {
          label: 'Circumferential Position (X)',
          points: [
            { label: 'Start', value: customInfo.location.startX },
            { label: 'End', value: customInfo.location.endX },
          ],
        },
        {
          label: 'Axial Position (Y)',
          points: [
            { label: 'Min', value: customInfo.location.endY },
            { label: 'Max', value: customInfo.location.startY },
          ],
          transform: Math.abs,
        },
      ]
        .map(
          (group: LocationGroup): ProcessedLocationGroup => ({
            ...group,
            points: group.points
              .filter(
                (p): p is { label: string; value: number } => !isNil(p.value)
              )
              .map((p) => {
                const millimeterValue = p.value
                const transform = group.transform
                const transformedValue = transform
                  ? transform(millimeterValue)
                  : millimeterValue
                const inchValue =
                  transformedValue * MILLIMETERS_TO_INCHES_CONVERSION

                return {
                  label: p.label,
                  value: GeckoMeasurementUnit.Inches.formatNumber({
                    num: inchValue,
                    maximumFractionDigits: MEASUREMENT_PRECISION_DIGITS,
                    locale: MEASUREMENT_LOCALE,
                    variant: undefined,
                  }),
                }
              }),
          })
        )
        .filter((group) => group.points.length > 0)
    : []

  return (
    <CardContainer>
      <Section>
        {summaryData.map(({ label, value }) => (
          <TextInput
            key={label}
            mode='read'
            $label={label}
            $labelLocation='side'
            defaultValue={value}
          />
        ))}
      </Section>

      {locationGroups.length > 0 && (
        <Section>
          <Text strong style={{ fontSize: '1.1rem' }}>
            Defect Location
          </Text>
          <LocationSection>
            {locationGroups.map((group) => (
              <LocationGroup key={group.label}>
                <LocationGroupLabel>{group.label}</LocationGroupLabel>
                {group.points.map((point) => (
                  <TextInput
                    key={point.label}
                    mode='read'
                    $label={point.label}
                    $labelLocation='side'
                    defaultValue={point.value}
                  />
                ))}
              </LocationGroup>
            ))}
          </LocationSection>
        </Section>
      )}

      {customInfo.comments && (
        <Section>
          <CommentsContainer>
            <CommentLabel>Comments</CommentLabel>
            <CommentValue>{customInfo.comments}</CommentValue>
          </CommentsContainer>
        </Section>
      )}
      {imageUrl && (
        <Section>
          <Text>Intensity Layer Defect Image</Text>
          <ImageContainer>
            <Image
              src={imageUrl}
              alt='Nick and Ding Indication'
              style={{ maxHeight: '300px' }}
            />
          </ImageContainer>
        </Section>
      )}
    </CardContainer>
  )
}
